import React, { useEffect, useRef } from "react";
import useOverviewContext from "../../store/use-overview-context";
import PropTypes from "prop-types";

/**
 * Component for implementing endless scrolling behavior.
 * This function uses the Apollo fetchMore function to add more data to the list of nodes.
 * Place it below the list of nodes in the overview component.
 * @returns {JSX.Element} The EndlessScroll component.
 */
const EndlessScroll = ({ rootMargin = 3000 }) => {
  // Get relevant values and functions from the OverviewContext.
  const { metaData, loading, fetchMore, items, config } = useOverviewContext();

  const scrollRef = useRef();

  /**
   * Triggers loading of next data chunk for endless scrolling.
   */
  const fetchMoreResults = () => {
    const newPage = Math.ceil(items.length / metaData.perPage);
    fetchMore({
      updateConfig: (prevConfig) => ({
        ...prevConfig,
        page: newPage,
      }),
      updateData: (prevData, newData) => {
        return {
          ...prevData,
          search_results: prevData.search_results.concat(
            // Append the new data to the existing data.
            newData.search_results
          ),
        };
      },
    });
  };

  useEffect(() => {
    const triggerElement = scrollRef.current;

    // Check if the element is intersecting with the viewport.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
            !loading && // Prevent fetching when loading.
            metaData.firstPageLoaded &&
            metaData.totalRows !== 0 && // Prevent fetching when there are no pages.
          (items.length / metaData.perPage) + 1 <= metaData.pageCount // Prevent fetching when all nodes are fetched.
        ) {
          fetchMoreResults();
        }
      },
      { threshold: 1.0, rootMargin: `${rootMargin}px` }
    );

    if (triggerElement) {
      observer.observe(triggerElement);
    }

    // Clean up function.
    return () => {
      if (triggerElement) {
        observer.disconnect();
      }
    };
  });

  return <div ref={scrollRef} data-beno="endless-scroll" />;
};

EndlessScroll.propTypes = {
  /**
   * The number in pixels that the intersection box will be increased.
   * A higher value will trigger the refetching function earlier.
   */
  rootMargin: PropTypes.number,
};

export default EndlessScroll;
